import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Sun, Moon, Smartphone, Languages, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { readConsent, grantAnalytics, denyAnalytics } from '../lib/consent';
import { Avatar } from '../components/Avatar';
import { invalidateAvatar } from '../lib/avatars';
import * as api from '../lib/api';
import { toast } from 'sonner';

// Enforced independently by the backend as well; this only spares the member a
// pointless upload. The wording is fixed.
const MAX_PHOTO_BYTES = 2621440;
const PHOTO_TOO_LARGE = 'This photo is larger than 2.5 MB. Please choose a photo under 2.5 MB.';

const APPEARANCE = [
  { value: 'light', key: 'settings.appearance.light', Icon: Sun },
  { value: 'dark', key: 'settings.appearance.dark', Icon: Moon },
  { value: 'system', key: 'settings.appearance.system', Icon: Smartphone },
];

// Parked until the Gagana Samoa wording has been reviewed. The localisation
// layer stays in place; only this control is hidden, so the app is English-only.
const SHOW_LANGUAGE = false;

const LANGUAGE_OPTIONS = [
  { value: 'en', key: 'settings.language.english' },
  { value: 'sm', key: 'settings.language.samoan' },
];

const splitName = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
};

export const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, refreshUser, signOut } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const initial = splitName(user?.name);
  const [first, setFirst] = useState(initial.first);
  const [last, setLast] = useState(initial.last);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(readConsent);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [confirmRemovePhoto, setConfirmRemovePhoto] = useState(false);
  const photoInputRef = useRef(null);
  const leaving = useRef(false);

  // Redirect an unauthenticated visitor, but never fight the sign-out/delete transition.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !leaving.current) {
      navigate('/signin', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Escape closes the destructive confirmation.
  useEffect(() => {
    if (!confirmDelete) return;
    const onKeyDown = (e) => e.key === 'Escape' && !deleting && setConfirmDelete(false);
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmDelete, deleting]);

  // The session may still be resolving on a direct visit, so mirror the name once it lands.
  useEffect(() => {
    const { first: f, last: l } = splitName(user?.name);
    setFirst(f);
    setLast(l);
  }, [user?.name]);

  if (isLoading || !isAuthenticated || !user) {
    return (
      <div className="page" data-testid="settings-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!first.trim()) {
      toast.error(t('settings.account.firstNameRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.updateMe({ name: [first.trim(), last.trim()].filter(Boolean).join(' ') });
      await refreshUser();
      toast.success(t('settings.account.saved'));
    } catch (err) {
      toast.error(t('settings.account.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleAnalytics = (allow) => {
    if (allow) grantAnalytics();
    else denyAnalytics();
    setAnalytics(allow ? 'granted' : 'denied');
    toast.success(t(allow ? 'settings.analytics.turnedOn' : 'settings.analytics.turnedOff'));
  };

  // Optional profile photo. The oversize check here is a courtesy only: the
  // backend enforces the same 2.5 MB limit independently, and the accept
  // filter below is UX assistance, never a boundary.
  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || photoBusy) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error(PHOTO_TOO_LARGE);
      return;
    }
    setPhotoBusy(true);
    try {
      await api.uploadMyPhoto(file);
      invalidateAvatar(user?.id);
      await refreshUser();
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || 'Could not update your photo. Please try again.'
      );
    } finally {
      setPhotoBusy(false);
    }
  };

  const handlePhotoRemove = async () => {
    setPhotoBusy(true);
    try {
      await api.deleteMyPhoto();
      invalidateAvatar(user?.id);
      await refreshUser();
      setConfirmRemovePhoto(false);
      toast.success('Profile photo removed');
    } catch {
      toast.error('Could not remove your photo. Please try again.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteAccount();
      leaving.current = true;
      signOut();
      toast.success(t('settings.delete.done'));
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(t('settings.delete.failed'));
      setDeleting(false);
    }
  };

  return (
    <div className="page" data-testid="settings-page">
      <header className="page-header">
        <div className="connections-header-top">
          <button className="back-btn" onClick={() => navigate('/profile')} aria-label={t('settings.back')} data-testid="settings-back">
            <ArrowLeft />
          </button>
          <h1 className="page-title">{t('settings.title')}</h1>
        </div>
      </header>

      <form className="add-form" onSubmit={handleSave} data-testid="settings-form">
        <h3 className="section-heading flush">{t('settings.account.heading')}</h3>

        <div className="form-group">
          <label className="form-label" htmlFor="first-name">{t('settings.account.firstName')}</label>
          <input
            id="first-name"
            type="text"
            className="form-input"
            value={first}
            maxLength={50}
            onChange={(e) => setFirst(e.target.value)}
            data-testid="input-first-name"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="last-name">{t('settings.account.lastName')}</label>
          <input
            id="last-name"
            type="text"
            className="form-input"
            value={last}
            maxLength={50}
            onChange={(e) => setLast(e.target.value)}
            data-testid="input-last-name"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="email">{t('settings.account.email')}</label>
          <input
            id="email"
            type="email"
            className="form-input"
            value={user.email}
            readOnly
            disabled
            aria-describedby="email-note"
            data-testid="settings-email"
          />
          <span className="form-hint" id="email-note">{t('settings.account.emailNote')}</span>
        </div>

        <button type="submit" className="form-submit" disabled={saving} data-testid="settings-save">
          {saving ? t('settings.account.saving') : t('settings.account.save')}
        </button>
      </form>

      <section className="appearance-section" data-testid="photo-section">
        <h3 className="section-heading flush">Profile photo</h3>
        <div className="photo-row">
          <Avatar
            userId={user?.id}
            name={user?.name}
            photoUrl={user?.photoUrl}
            className="identity-avatar"
            testId="settings-avatar"
          />
          <div className="photo-actions">
            <button
              type="button"
              className="photo-btn"
              onClick={() => photoInputRef.current?.click()}
              disabled={photoBusy}
              data-testid="photo-upload-btn"
            >
              {photoBusy ? 'Working...' : user?.photoUrl ? 'Change photo' : 'Add photo'}
            </button>
            {user?.photoUrl && (
              <button
                type="button"
                className="photo-btn danger"
                onClick={() => setConfirmRemovePhoto(true)}
                disabled={photoBusy}
                data-testid="photo-remove-btn"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
        <input
          ref={photoInputRef}
          type="file"
          className="photo-input"
          accept="image/jpeg,image/png,image/webp"
          onChange={handlePhotoPick}
          data-testid="photo-file-input"
        />
        <span className="form-hint" data-testid="photo-hint">
          Optional. JPEG, PNG or WebP, up to 2.5 MB. Your photo appears as a small circle
          beside your name.
        </span>
      </section>

      <section className="appearance-section" data-testid="appearance-section">
        <h3 className="section-heading flush">{t('settings.appearance.heading')}</h3>
        <div className="segmented" role="radiogroup" aria-label={t('settings.appearance.heading')}>
          {APPEARANCE.map(({ value, key, Icon }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={theme === value}
              className={`segmented-option ${theme === value ? 'active' : ''}`}
              onClick={() => setTheme(value)}
              data-testid={`appearance-option-${value}`}
            >
              <Icon className="segmented-icon" aria-hidden="true" />
              {t(key)}
            </button>
          ))}
        </div>
        <span className="form-hint" data-testid="appearance-hint">
          {theme === 'system'
            ? t('settings.appearance.followingDevice', {
                mode: t(`settings.appearance.${resolvedTheme}`),
              })
            : t('settings.appearance.savedOnDevice')}
        </span>
      </section>

      <section className="appearance-section" data-testid="analytics-section">
        <h3 className="section-heading flush">{t('settings.analytics.heading')}</h3>
        <div className="segmented two-up" role="radiogroup" aria-label={t('settings.analytics.heading')}>
          {[true, false].map((allow) => (
            <button
              key={allow ? 'granted' : 'denied'}
              type="button"
              role="radio"
              aria-checked={analytics === (allow ? 'granted' : 'denied')}
              className={`segmented-option ${analytics === (allow ? 'granted' : 'denied') ? 'active' : ''}`}
              onClick={() => handleAnalytics(allow)}
              data-testid={`analytics-option-${allow ? 'granted' : 'denied'}`}
            >
              <BarChart3 className="segmented-icon" aria-hidden="true" />
              {t(allow ? 'settings.analytics.allow' : 'settings.analytics.decline')}
            </button>
          ))}
        </div>
        <span className="form-hint" data-testid="analytics-hint">
          {t(analytics === 'granted' ? 'settings.analytics.hintGranted' : 'settings.analytics.hintDenied')}
        </span>
      </section>

      {SHOW_LANGUAGE && (
      <section className="appearance-section" data-testid="language-section">
        <h3 className="section-heading flush">{t('settings.language.heading')}</h3>
        <div className="segmented two-up" role="radiogroup" aria-label={t('settings.language.heading')}>
          {LANGUAGE_OPTIONS.map(({ value, key }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={language === value}
              className={`segmented-option ${language === value ? 'active' : ''}`}
              onClick={() => setLanguage(value)}
              data-testid={`language-option-${value}`}
            >
              <Languages className="segmented-icon" aria-hidden="true" />
              {t(key)}
            </button>
          ))}
        </div>
        <span className="form-hint" data-testid="language-hint">
          {t('settings.language.hint')}
        </span>
      </section>
      )}

      {user?.canModerate && (
      <section className="appearance-section" data-testid="moderation-section">
        <h3 className="section-heading flush">Moderation</h3>
        <p className="form-hint">Review recommendations that members have reported.</p>
        <button
          className="placeholder-btn"
          onClick={() => navigate('/moderation')}
          data-testid="settings-moderation-entry"
        >
          Open the report queue
        </button>
      </section>
      )}

      <section className="appearance-section" data-testid="contact-settings-section">
        <h3 className="section-heading flush">Contact us</h3>
        <p className="form-hint">Get in touch with irecommend.</p>
        <button
          className="placeholder-btn"
          onClick={() => navigate('/contact')}
          data-testid="settings-contact-entry"
        >
          Contact us
        </button>
      </section>

      <section className="appearance-section" data-testid="legal-settings-section">
        <h3 className="section-heading flush">Legal</h3>
        <p className="form-hint">How irecommend works and how your information is handled.</p>
        <button
          className="placeholder-btn"
          onClick={() => navigate('/privacy')}
          data-testid="settings-privacy-entry"
        >
          Privacy Statement
        </button>
        <button
          className="placeholder-btn"
          onClick={() => navigate('/terms')}
          data-testid="settings-terms-entry"
        >
          Terms of Use
        </button>
      </section>

      <section className="danger-zone">
        <h3 className="section-heading flush">{t('settings.delete.heading')}</h3>
        <p className="form-hint">{t('settings.delete.copy')}</p>
        <button
          className="danger-btn"
          onClick={() => setConfirmDelete(true)}
          data-testid="delete-account-btn"
        >
          {t('settings.delete.button')}
        </button>
      </section>

      {confirmRemovePhoto && (
        <div className="limit-modal-overlay" data-testid="photo-remove-confirm">
          <div className="limit-modal" role="dialog" aria-modal="true" aria-labelledby="photo-remove-title">
            <h3 className="limit-modal-title" id="photo-remove-title">Remove your photo?</h3>
            <p className="limit-modal-text">
              Your photo will be deleted and your initials will be shown instead.
            </p>
            <div className="limit-modal-actions">
              <button
                className="limit-modal-btn secondary"
                onClick={() => setConfirmRemovePhoto(false)}
                disabled={photoBusy}
                autoFocus
                data-testid="photo-remove-cancel"
              >
                Cancel
              </button>
              <button
                className="limit-modal-btn primary danger"
                onClick={handlePhotoRemove}
                disabled={photoBusy}
                data-testid="photo-remove-confirm-btn"
              >
                {photoBusy ? 'Removing...' : 'Remove photo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="limit-modal-overlay" data-testid="delete-account-confirm">
          <div className="limit-modal" role="dialog" aria-modal="true" aria-labelledby="delete-title">
            <h3 className="limit-modal-title" id="delete-title">{t('settings.delete.confirmTitle')}</h3>
            <p className="limit-modal-text">{t('settings.delete.confirmCopy')}</p>
            <div className="limit-modal-actions">
              <button
                className="limit-modal-btn secondary"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                autoFocus
                data-testid="delete-account-cancel"
              >
                {t('settings.delete.cancel')}
              </button>
              <button
                className="limit-modal-btn primary danger"
                onClick={handleDelete}
                disabled={deleting}
                data-testid="delete-account-confirm-btn"
              >
                {deleting ? t('settings.delete.deleting') : t('settings.delete.button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
